import type {
  ContentBundleEnvelope,
  DisasterModeFixtureBundlePayload,
  SearchableLibraryRecord,
  SupportedLocale,
} from "@safesphere/contracts";
import { assessPublicationEligibility } from "@safesphere/editorial";

import { getBundleStorageSnapshot } from "../offline/database.js";
import { verifyContentBundle } from "../offline/verifyBundle.js";

export type LibraryContentStatus = "VERIFIED" | "STALE_VERIFIED";
export type LibraryMatchReason = "TITLE" | "TAXONOMY" | "ALIAS" | "SUMMARY";

export interface IndexedLibraryRecord extends SearchableLibraryRecord {
  readonly status: LibraryContentStatus;
  readonly normalized: {
    readonly title: string;
    readonly summary: string;
    readonly category: string;
    readonly topic: string;
    readonly aliases: readonly string[];
  };
}

export interface OfflineLibraryIndex {
  readonly bundleId: string;
  readonly locale: SupportedLocale;
  readonly generation: string;
  readonly source: "ACTIVE" | "KNOWN_GOOD";
  readonly records: readonly IndexedLibraryRecord[];
}

export interface LibrarySearchResult {
  readonly record: IndexedLibraryRecord;
  readonly score: number;
  readonly matchedBy: readonly LibraryMatchReason[];
}

export type LibraryIndexResolution =
  | { readonly ok: true; readonly index: OfflineLibraryIndex }
  | { readonly ok: false; readonly reason: "UNAVAILABLE" | "UNTRUSTED" | "INVALID_PAYLOAD" };

const punctuation = /[\p{P}\p{S}]+/gu;
const whitespace = /\s+/gu;

export function normalizeLibraryText(value: string, locale: SupportedLocale): string {
  const normalized = value.normalize("NFKC").toLocaleLowerCase(locale).replace(punctuation, " ");
  const collapsed = normalized.replace(whitespace, " ").trim();
  return locale === "th" ? collapsed.replace(whitespace, "") : collapsed;
}

function payload(value: unknown): value is DisasterModeFixtureBundlePayload {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<DisasterModeFixtureBundlePayload>;
  return (
    candidate.fixtureType === "DISASTER_MODE_NON_AUTHORITATIVE" && Array.isArray(candidate.records)
  );
}

function indexRecord(
  record: DisasterModeFixtureBundlePayload["records"][number],
  locale: SupportedLocale,
  now: number,
): IndexedLibraryRecord | undefined {
  const library = (record as { readonly library?: SearchableLibraryRecord }).library;
  if (
    library?.locale !== locale ||
    library.revisionId !== record.revisionId ||
    library.contentHash !== record.contentHash ||
    library.title.trim().length === 0 ||
    library.summary.trim().length === 0 ||
    Number.isNaN(Date.parse(library.reviewedAt))
  ) {
    return undefined;
  }
  const assessment = assessPublicationEligibility(record.governance, now);
  const blocking = assessment.missingRequirements.filter(
    (reason) => reason !== "REVIEW_STALE" && reason !== "SOURCE_STALE",
  );
  if (blocking.length > 0) return undefined;
  const stale = assessment.reviewFreshness === "STALE" || assessment.sourceFreshness === "STALE";
  return {
    ...library,
    status: stale ? "STALE_VERIFIED" : "VERIFIED",
    normalized: {
      title: normalizeLibraryText(library.title, locale),
      summary: normalizeLibraryText(library.summary, locale),
      category: normalizeLibraryText(library.category.label, locale),
      topic: normalizeLibraryText(library.topic.label, locale),
      aliases: library.approvedAliases.map((alias) => normalizeLibraryText(alias, locale)),
    },
  };
}

async function build(
  envelope: ContentBundleEnvelope,
  locale: SupportedLocale,
  source: OfflineLibraryIndex["source"],
  now: number,
): Promise<LibraryIndexResolution> {
  const verification = await verifyContentBundle(envelope);
  if (!verification.ok) return { ok: false, reason: "UNTRUSTED" };
  if (envelope.body.locale !== locale || !payload(envelope.body.payload))
    return { ok: false, reason: "INVALID_PAYLOAD" };
  const records = envelope.body.payload.records
    .map((record) => indexRecord(record, locale, now))
    .filter((record): record is IndexedLibraryRecord => record !== undefined)
    .toSorted((left, right) => left.id.localeCompare(right.id));
  return {
    ok: true,
    index: {
      bundleId: envelope.body.bundleId,
      locale,
      generation: `${envelope.body.bundleId}:${envelope.body.contentVersion}:${locale}`,
      source,
      records,
    },
  };
}

export async function resolveOfflineLibraryIndex(
  locale: SupportedLocale,
  now = Date.now(),
): Promise<LibraryIndexResolution> {
  const snapshot = await getBundleStorageSnapshot(locale);
  if (snapshot.activeBundle !== undefined) {
    const active = await build(snapshot.activeBundle.bundle, locale, "ACTIVE", now);
    if (active.ok) return active;
  }
  if (snapshot.knownGoodBundle !== undefined)
    return build(snapshot.knownGoodBundle.bundle, locale, "KNOWN_GOOD", now);
  return { ok: false, reason: "UNAVAILABLE" };
}

function matches(field: string, query: string): boolean {
  return query.length > 0 && field.includes(query);
}

export function searchLibrary(
  index: OfflineLibraryIndex,
  queryValue: string,
): readonly LibrarySearchResult[] {
  const query = normalizeLibraryText(queryValue, index.locale);
  if (query.length === 0) return [];
  return index.records
    .map((record) => {
      const reasons: LibraryMatchReason[] = [];
      let score = 0;
      if (matches(record.normalized.title, query)) {
        reasons.push("TITLE");
        score += 400;
      }
      if (matches(record.normalized.topic, query) || matches(record.normalized.category, query)) {
        reasons.push("TAXONOMY");
        score += 300;
      }
      if (record.normalized.aliases.some((alias) => matches(alias, query))) {
        reasons.push("ALIAS");
        score += 200;
      }
      if (matches(record.normalized.summary, query)) {
        reasons.push("SUMMARY");
        score += 100;
      }
      return { record, score, matchedBy: reasons };
    })
    .filter((result) => result.score > 0)
    .toSorted(
      (left, right) => right.score - left.score || left.record.id.localeCompare(right.record.id),
    );
}

export function libraryCategories(index: OfflineLibraryIndex) {
  return [
    ...new Map(index.records.map((record) => [record.category.id, record.category])).values(),
  ].toSorted((left, right) => left.label.localeCompare(right.label, index.locale));
}

export function libraryTopics(index: OfflineLibraryIndex, categoryId?: string) {
  const records =
    categoryId === undefined
      ? index.records
      : index.records.filter((record) => record.category.id === categoryId);
  return [...new Map(records.map((record) => [record.topic.id, record.topic])).values()].toSorted(
    (left, right) => left.label.localeCompare(right.label, index.locale),
  );
}
